import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import Constants from "../utils/constants";
import { useForm } from "../utils/hooks";
import WithLoginLayout from "../hocs/layouts/LoginLayout";
import {
  resetPass,
  validateGuidPass,
  validarCodigoRecuperacion,
  completarResetearClaveConCodigo,
} from "../services/user";
import Reaptcha from "reaptcha";
import { useGlobalStore } from "../utils/global";
import InputMask from "react-input-mask";

const ResetPassword = () => {
  const { showNotification } = useGlobalStore();
  var captchaRef;

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [guid, setGuid] = useState<string | null>(null);
  const [useCodeFlow, setUseCodeFlow] = useState(false);
  const [cedulaFromRecover, setCedulaFromRecover] = useState<string>("");
  const [codeValidated, setCodeValidated] = useState(false);
  const [codigoVerificacion, setCodigoVerificacion] = useState<string>("");
  const [succeed, setSucceed] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [newPasswordShown, setNewPasswordShown] = useState(false);
  const [confirmNewPasswordShown, setConfirmNewPasswordShown] = useState(false);
  const [formState, setFormError, changeHandler, reset] = useForm({
    cedula: "",
    codigoVerificacion: "",
    newPass: "",
    confirmNewPass: "",
    captcha: false,
  });

  useEffect(() => {
    captchaRef?.reset();

    const guidParam: string | null = searchParams.get("guid");
    const cedulaParam = searchParams.get("cedula");
    const cedulaState = (location.state as { cedula?: string })?.cedula;

    if (guidParam) {
      setGuid(guidParam);
      setUseCodeFlow(false);
      validateGuidPass(guidParam).then((res) => {
        if (res && !res.data?.successed) {
          setTokenInvalid(true);
          setError(res.data?.message?.[0]?.message ?? "Token inválido.");
        }
      });
    } else {
      setUseCodeFlow(true);
      setCedulaFromRecover(cedulaState ?? cedulaParam ?? "");
      if (cedulaState) changeHandler("cedula", cedulaState)();
      else if (cedulaParam) changeHandler("cedula", cedulaParam)();
    }
  }, []);

  function disableButton() {
    if (useCodeFlow && !codeValidated) {
      const cedula = (formState.data.cedula ?? cedulaFromRecover).replace(/-/g, "").replace(/_/g, "");
      return cedula.length !== 11 || (formState.data.codigoVerificacion ?? "").length !== 6;
    }
    return (
      !formState.data.newPass ||
      !formState.data.confirmNewPass ||
      formState.data.newPass !== formState.data.confirmNewPass ||
      !formState.data.captcha
    );
  }

  function submitCodeStep() {
    const cedula = (formState.data.cedula ?? cedulaFromRecover).replace(/-/g, "").replace(/_/g, "");
    const code = (formState.data.codigoVerificacion ?? "").trim();
    validarCodigoRecuperacion(cedula, code).then((res) => {
      if (res?.data?.successed) {
        setCodigoVerificacion(code);
        setCodeValidated(true);
        setError("");
      } else {
        setError(res?.data?.message?.[0]?.message ?? "Código inválido.");
      }
    });
    captchaRef?.reset();
  }

  function submitForm() {
    if (useCodeFlow && !codeValidated) {
      submitCodeStep();
      return;
    }

    if (useCodeFlow) {
      const cedula = (formState.data.cedula ?? cedulaFromRecover).replace(/-/g, "").replace(/_/g, "");
      completarResetearClaveConCodigo(cedula, codigoVerificacion, formState.data.newPass).then(
        (res) => {
          if (res?.data?.successed) {
            setSucceed(res.data?.message?.[0]?.message ?? "Su contraseña se ha cambiado exitosamente.");
          } else {
            setError(res?.data?.message?.[0]?.message ?? "Error al cambiar la contraseña.");
          }
        }
      );
    } else {
      resetPass(formState.data.newPass, guid!).then((res) => {
        if (res?.data?.successed) {
          setSucceed(res.data?.message?.[0]?.message ?? "Su contraseña se ha cambiado exitosamente.");
        } else {
          setError(res?.data?.message?.[0]?.message ?? "Error al cambiar la contraseña.");
        }
      });
    }

    changeHandler("captcha", false)();
    captchaRef?.reset();
  }

  function onVerify(recaptchaResponse) {
    changeHandler("captcha", true)();
  }

  const showCodeStep = useCodeFlow && !codeValidated;
  const showPasswordStep =
    ((useCodeFlow && codeValidated) || (!!guid && !tokenInvalid)) && succeed.length === 0;
  // Solo mostrar pantalla completa "Enlace erróneo" cuando el token/guid es inválido; no cuando falla validación de contraseña
  const showFullPageError = tokenInvalid && error.length > 0;

  return (
    <>
      {showCodeStep && (
        <>
          <Link
            to={Constants.APPURLS.RECOVER_PASSWORD}
            className="btn btn-secondary btn-medium"
          >
            <i className="icon icon-arrow-leftv-short right"></i>Volver
          </Link>
          <div className="block pt-6 md:pt-14 pb-10">
            <h1 className="text-primary-100 font-bold mb-2">Ingresar código</h1>
            <p className="text-gray-70 text-body-1 font-body font-normal">
              Revisa tu correo e ingresa el <span className="font-semibold">código de 6 dígitos</span> que te enviamos.
            </p>
          </div>
          {error.length > 0 && (
            <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-body-2" role="alert">
              {error}
            </div>
          )}
          <form
            className="block space-y-8"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="form-group">
              <label>Documento de Identidad</label>
              <InputMask
                type="text"
                className="form-control large"
                placeholder="Ej: 001-1234567-8"
                value={formState.data.cedula || cedulaFromRecover}
                onChange={(e) => {
                  if (error) setError("");
                  changeHandler("cedula", undefined, "-")(e);
                }}
                mask="999-9999999-9"
                maskPlaceholder={"_"}
                readOnly={!!cedulaFromRecover}
              />
            </div>
            <div className="form-group">
              <label>Código de verificación (6 dígitos)</label>
              <input
                type="text"
                className="form-control large"
                placeholder="000000"
                maxLength={6}
                value={formState.data.codigoVerificacion}
                onChange={(e) => {
                  if (error) setError("");
                  const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                  changeHandler("codigoVerificacion", v)();
                }}
                inputMode="numeric"
              />
            </div>
            <button
              type="button"
              onClick={submitCodeStep}
              disabled={disableButton()}
              className="btn btn-primary btn-large w-full"
            >
              Continuar
            </button>
          </form>
        </>
      )}
      {showPasswordStep && succeed.length === 0 && (
        <>
          <Link
            to={Constants.APPURLS.LOGIN}
            className="btn btn-secondary btn-medium"
          >
            <i className="icon icon-arrow-leftv-short right"></i>Oficina virtual
          </Link>

          <div className="block pt-6 md:pt-14 pb-10">
            <h1 className="text-primary-100 font-bold mb-2">
              Resetear Contraseña
            </h1>
            <p className="text-gray-70 text-body-1 font-body font-normal">
              Antes de continuar favor de{" "}
              <span className="font-semibold">
                ingresar su nueva contraseña
              </span>
            </p>
          </div>
          {error.length > 0 && (
            <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-body-2" role="alert">
              {error}
            </div>
          )}
          <form
            className="block space-y-8"
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div className="form-group">
              <label>Nueva Contraseña</label>
              <div className="form-icon">
                <i
                  onClick={() => setNewPasswordShown(!newPasswordShown)}
                  className={`icon icon-eye${
                    newPasswordShown ? "" : "off"
                  } right text-gray-80 icon-password text-[10px]`}
                ></i>
                <input
                  type={newPasswordShown ? "text" : "password"}
                  className="form-control large"
                  placeholder="Ingresa tu nueva contraseña"
                  value={formState.data.newPass}
                  onChange={(e) => {
                    if (error) setError("");
                    changeHandler("newPass")(e);
                  }}
                />
              </div>
              <small>Debe tener al menos 8 caracteres, una mayúscula y un número.</small>
            </div>
            <div className="form-group">
              <label>Confirmación de Contraseña</label>
              <div className="form-icon">
                <i
                  onClick={() =>
                    setConfirmNewPasswordShown(!confirmNewPasswordShown)
                  }
                  className={`icon icon-eye${
                    confirmNewPasswordShown ? "" : "off"
                  } right text-gray-80 icon-password text-[10px]`}
                ></i>
                <input
                  type={confirmNewPasswordShown ? "text" : "password"}
                  className="form-control large"
                  placeholder="Confirma tu nueva contraseña"
                  value={formState.data.confirmNewPass}
                  onChange={(e) => {
                    if (error) setError("");
                    changeHandler("confirmNewPass")(e);
                  }}
                />
              </div>
              <small>Ambas contraseñas deben de ser iguales</small>
            </div>
            <Reaptcha
              ref={(e) => (captchaRef = e)}
              sitekey={process.env.REACT_APP_SITE_KEY}
              onVerify={onVerify}
            />
            <button
              type="button"
              onClick={submitForm}
              disabled={disableButton()}
              className="btn btn-primary btn-large w-full"
            >
              Continuar
            </button>
          </form>
        </>
      )}
      {succeed.length > 0 && (
        <div className="block text-center">
          <div className="w-20 h-20 rounded-full border-2 border-primary-90 p-2 mx-auto mb-10">
            <i className="icon icon-check  rounded-full bg-primary-20 text-primary-100 text-[20px] w-full h-full flex items-center justify-center"></i>
          </div>
          <h1 className="text-primary-100 font-bold mb-2">Contraseña cambiada</h1>
          <p className="text-gray-70 text-body-1 font-body font-normal mb-12">
            {succeed}
          </p>
          <Link
            to={Constants.APPURLS.LOGIN}
            className="btn btn-primary btn-large px-4 md:px-[112px]"
          >
            Ir a Inicio de Sesión
          </Link>
        </div>
      )}
      {showFullPageError && (
        <div className="block text-center">
          <div className="w-20 h-20 rounded-full border-2 border-red-200 p-2 mx-auto mb-10">
            <i className="icon icon-close rounded-full bg-red-50 text-red-600 text-[20px] w-full h-full flex items-center justify-center"></i>
          </div>
          <h1 className="text-primary-100 font-bold mb-2">Enlace inválido</h1>
          <p className="text-gray-70 text-body-1 font-body font-normal mb-12">
            {error}
          </p>
          <Link
            to={Constants.APPURLS.RECOVER_PASSWORD}
            className="btn btn-secondary btn-large px-4 md:px-[112px]"
          >
            Volver a solicitar recuperación
          </Link>
        </div>
      )}
    </>
  );
};

export default WithLoginLayout(ResetPassword);
